import React, { useState, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { Button, Menu, MailSolid, CheckSolid, CaretDown, Tooltip } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useDisplayTheme } from 'hooks/useUiState'
import { useLiveReportInfo } from 'hooks/useLiveReportConfigModalState'

import { AutomationId } from 'utils/automationUtil'
import { useAppSelector } from 'app/hooks'
import { selectV4ScheduledReportInfo } from 'features/scheduledReport/selectors'
import { SCHEDULED_REPORT_BUTTON_FLAG } from 'app/featureFlags'
import { useBooleanVariationFromFeatureFlag } from 'hooks/useBooleanVariationFromFeatureFlag'
import useActionPermissions from 'pages/dashboard/view/hooks/useActionPermissions'

const StyledButton = styled(Button)`
  white-space: nowrap;
`

const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledMenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const StyledPlaceholder = styled.div`
  width: 16px;
  height: 16px;
`

const ReportButton = ({
  onLiveReportClick,
  onScheduledReportClick
}: {
  onLiveReportClick?: () => void
  onScheduledReportClick?: () => void
}) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()
  const liveReportInfo = useLiveReportInfo()
  const scheduledReportInfo = useAppSelector(selectV4ScheduledReportInfo)
  const hasShowScheduledReportButtonPermission = useBooleanVariationFromFeatureFlag(
    SCHEDULED_REPORT_BUTTON_FLAG
  )

  const { liveReportPermission } = useActionPermissions()

  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)

  const handleClose = () => {
    setOpen(false)
  }

  const handleLiveReport = () => {
    setOpen(false)
    onLiveReportClick?.()
  }

  const handleScheduledReport = () => {
    setOpen(false)
    onScheduledReportClick?.()
  }

  return (
    <>
      <StyledButton
        ref={buttonRef}
        mode={theme}
        color="secondary"
        startIcon={<MailSolid fontSize={16} />}
        data-testid={AutomationId.dashboard.report.button}
        onClick={() => {
          setOpen(true)
        }}>
        <Tooltip
          key={uuidv4()}
          mode={theme}
          content={t('reportButtonTooltipContent')}
          placement="bottom"
          followCursor={false}
          offset={[0, 0]}
          interactive
          enabled={!open}>
          <StyledButtonContent>
            {t('report')}
            <CaretDown fontSize={16} color={sys[theme].text.weak} />
          </StyledButtonContent>
        </Tooltip>
      </StyledButton>
      <Menu
        mode={theme}
        open={open}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        placement="bottom-end">
        {liveReportPermission && (
          <Menu.MenuItem onClick={handleLiveReport}>
            <StyledMenuItem>
              {t('liveReport')}{' '}
              {liveReportInfo.reportEnabled ? (
                <CheckSolid color={sys[theme].interaction.primary.default} />
              ) : (
                scheduledReportInfo && <StyledPlaceholder />
              )}
            </StyledMenuItem>
          </Menu.MenuItem>
        )}
        {hasShowScheduledReportButtonPermission && (
          <Menu.MenuItem onClick={handleScheduledReport}>
            <StyledMenuItem>
              {t('scheduledReport')}
              {scheduledReportInfo ? (
                <CheckSolid color={sys[theme].interaction.primary.default} />
              ) : (
                liveReportInfo.reportEnabled && <StyledPlaceholder />
              )}
            </StyledMenuItem>
          </Menu.MenuItem>
        )}
      </Menu>
    </>
  )
}

export default ReportButton
